import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges } from '@angular/core';
import { HttpClient, HttpBackend } from '@angular/common/http';
import { interval, Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FireForesightService, DataService, SiteTwinService, PublishedCameraWatchService } from 'src/api/services';
import { Camera, CameraImage, Incident } from 'src/api/models';
import { ApiConfiguration } from 'src/api/api-configuration';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { WatchViewItem } from '../../views/applications/fire-foresight/watch/fire-foresight-watch.component';

@Component({
  selector: 'app-watch-camera-view',
  templateUrl: './watch-camera-view.component.html',
  styleUrls: ['./watch-camera-view.component.scss']
})
export class WatchCameraViewComponent implements OnInit, OnDestroy, OnChanges {
  @Input() item!: WatchViewItem;
  @Input() refreshTrigger$!: Subject<void>;
  @Input() rotationTrigger$!: Subject<void>;
  @Input() enableRotation: boolean = true; // Default to true for backward compatibility
  @Input() hideRemove: boolean = false; // Hide remove button in public view
  @Input() publishedIdentifier: string | null = null; // Identifier for published/public views
  @Output() remove = new EventEmitter<WatchViewItem>();

  private destroy$ = new Subject<void>();

  public currentImageUrl: string = '';
  public nextImageUrl: string = '';
  public overlayConfig: string | null = null;
  public overlays: any[] = [];
  public isZoomedImage: boolean = false;
  public isTransitioning = false;
  public transitionDirection: 'forward' | 'backward' = 'forward';
  public isFullscreen = false;
  public loading = true;
  public error = false;
  public cameraName: string = '';
  
  // For camera rotation
  public cameraImages: CameraImage[] = [];
  private currentImageIndex = 0;
  private preloadedUrls = new Set<string>();

  // For incidents
  public incident?: Incident;

  // For incident-status type
  public statusIncidents: Incident[] = [];
  private currentStatusIncidentIndex = 0;
  private statusRotationSubscription?: Subscription;
  private statusRefreshSubscription?: Subscription;
  private readonly STATUS_ROTATION_INTERVAL = 30000; // 30 seconds
  private readonly STATUS_REFRESH_INTERVAL = 60000; // 1 minute - refresh incidents list
  private knownIncidentIds: Set<number> = new Set(); // Track which incidents we've seen

  // Services for unauthenticated/published access
  private openPublishedCameraWatchService: PublishedCameraWatchService;
  private openFireForesightService: FireForesightService;
  private openDataService: DataService;

  constructor(
    public app: ApplicationConfigService,
    private readonly httpBackend: HttpBackend,
    private fireForesightService: FireForesightService,
    private dataService: DataService,
    private siteTwinService: SiteTwinService
  ) {
    // Create HTTP clients that bypass interceptors for public/published views
    const http = new HttpClient(httpBackend);
    const config = new ApiConfiguration();
    this.openPublishedCameraWatchService = new PublishedCameraWatchService(config, http);
    this.openFireForesightService = new FireForesightService(config, http);
    this.openDataService = new DataService(config, http);
  }

  ngOnInit(): void {
    this.loadItemData();
    
    // Subscribe to refresh trigger
    if (this.refreshTrigger$) {
      this.refreshTrigger$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.refreshImage());
    }

    // Subscribe to rotation trigger only if rotation is enabled
    if (this.rotationTrigger$ && this.enableRotation) {
      this.rotationTrigger$
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.rotateToNextImage());
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['item'] && !changes['item'].firstChange) {
      this.loadItemData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    
    if (this.statusRotationSubscription) {
      this.statusRotationSubscription.unsubscribe();
    }
    if (this.statusRefreshSubscription) {
      this.statusRefreshSubscription.unsubscribe();
    }
  }

  private loadItemData(showLoading: boolean = true): void {
    if (showLoading) {
      this.loading = true;
      this.error = false;
    }

    if (this.item.type === 'camera') {
      this.loadCamera(showLoading);
    } else if (this.item.type === 'incident') {
      this.loadIncident(showLoading);
    } else if (this.item.type === 'incident-status') {
      this.loadIncidentStatus(showLoading);
    }
  }

  private loadCamera(showLoading: boolean = true): void {
    // Clear overlays for cameras (they don't have detection metadata)
    this.overlayConfig = null;
    this.overlays = [];
    
    // Check if this is a generic camera (non-FireForesight)
    if (!this.enableRotation && this.item.uri) {
      // Generic camera - use DataService to get latest image
      this.loadGenericCamera(showLoading);
      return;
    }
    
    // FireForesight camera - use appropriate service based on publishedIdentifier
    if (showLoading) {
      this.loading = true;
      this.error = false;
    }

    let apiCall;
    if (this.publishedIdentifier) {
      apiCall = this.openPublishedCameraWatchService.PublishedCameraWatchGetCamera({
        identifier: this.publishedIdentifier,
        siteId: parseInt(this.item.id, 10)
      });
    } else {
      apiCall = this.fireForesightService.FireForesightGetCamera({
        customerId: this.app.customerId,
        siteId: parseInt(this.item.id, 10)
      });
    }
    
    apiCall.subscribe({
      next: (camera: Camera) => {
        this.cameraName = camera.name || this.item.name;
        
        if (camera.images && camera.images.length > 0) {
          // Sort images by bearing for clockwise rotation
          this.cameraImages = [...camera.images].sort((a, b) => 
            (a.bearing || 0) - (b.bearing || 0)
          );
          // Preserve current index if refreshing, otherwise start at 0
          if (showLoading) {
            this.currentImageIndex = 0;
          }
          this.updateCurrentImage();
        } else {
          if (showLoading) {
            this.error = true;
            this.cameraName = camera.name || 'No images available';
          }
        }
        
        if (showLoading) {
          this.loading = false;
        }
      },
      error: (err) => {
        console.error('Error loading camera:', err);
        if (showLoading) {
          this.error = true;
          this.loading = false;
          this.cameraName = 'Error loading camera';
        }
      }
    });
  }

  private loadGenericCamera(showLoading: boolean = true): void {
    // Use the URI from the item to fetch the latest image
    if (!this.item.uri) {
      console.error('No URI provided for generic camera');
      if (showLoading) {
        this.error = true;
        this.loading = false;
        this.cameraName = 'No URI available';
      }
      return;
    }

    this.cameraName = this.item.name;

    if (showLoading) {
      this.loading = true;
      this.error = false;
    }

    // Fetch latest image from the last hour using appropriate service
    const request = {
      period: '-1h:1h', // Query last hour to get recent images
      type: 'media',
      timeZoneOffset: this.app.getTimeZoneOffset(null),
      forceEmbed: false,
      index: 0,
      publishedIdentifier: null,
      siteUri: null,
      timeSeries: {
        single: {
          variable: {
            uri: this.item.uri,
            transforms: null,
          }
        }
      }
    };

    let apiCall;
    if (this.publishedIdentifier) {
      apiCall = this.openPublishedCameraWatchService.PublishedCameraWatchGetData({
        identifier: this.publishedIdentifier,
        request
      });
    } else {
      apiCall = this.dataService.DataGetData({
        customerId: this.app.customerId,
        request
      });
    }

    apiCall.subscribe({
      next: (r) => {
        if (r.values && r.values.length > 0) {
          // Use the last (most recent) image returned
          this.currentImageUrl = r.values[r.values.length - 1].valueStr;
          console.log('[WatchCameraView] Generic camera loaded:', this.cameraName, 'with', r.values.length, 'images');
        } else {
          console.warn('[WatchCameraView] No images returned for generic camera:', this.cameraName);
          if (showLoading) {
            this.error = true;
            this.cameraName = this.item.name + ' - No images available';
          }
        }
        
        if (showLoading) {
          this.loading = false;
        }
      },
      error: (err) => {
        console.error('[WatchCameraView] Error loading generic camera:', err);
        if (showLoading) {
          this.error = true;
          this.loading = false;
          this.cameraName = 'Error loading camera';
        }
      }
    });
  }

  private loadIncident(showLoading: boolean = true): void {
    if (showLoading) {
      this.loading = true;
      this.error = false;
    }

    // Use appropriate service based on publishedIdentifier
    let apiCall;
    if (this.publishedIdentifier) {
      apiCall = this.openPublishedCameraWatchService.PublishedCameraWatchGetIncident({
        identifier: this.publishedIdentifier,
        incidentId: parseInt(this.item.id, 10)
      });
    } else {
      apiCall = this.fireForesightService.FireForesightGetIncident({
        customerId: this.app.customerId,
        incidentId: parseInt(this.item.id, 10)
      });
    }
    
    apiCall.subscribe({
      next: (incident: Incident) => {
        this.incident = incident;
        this.cameraName = this.item.name;
        
        // Extract detection metadata for overlay
        console.log('[WatchCameraView] Incident detections:', incident.detections?.length || 0);
        if (incident.detections && incident.detections.length > 0) {
          this.overlayConfig = incident.detections[incident.detections.length - 1].detectionMetaData;
          console.log('[WatchCameraView] Overlay config set:', this.overlayConfig);
        } else {
          this.overlayConfig = null;
          console.log('[WatchCameraView] No detections available for overlay');
        }
        
        // Try zoomedImageFeedUri first, then imageFeedUri, then static fallbacks
        const zoomedImageUri = incident.zoomedImageFeedUri;
        const imageUri = incident.imageFeedUri;
        
        if (zoomedImageUri) {
          // Try zoomed image feed first
          this.fetchLatestImageFromUri(zoomedImageUri).subscribe({
            next: (imageUrl) => {
              if (imageUrl) {
                this.currentImageUrl = imageUrl;                this.isZoomedImage = true;                if (showLoading) {
                  this.loading = false;
                }
              } else {
                // No results from zoomed feed, try regular feed
                this.tryRegularImageFeed(incident, imageUri, showLoading);
              }
            },
            error: (err) => {
              console.error('Error loading zoomed incident images:', err);
              // Error with zoomed feed, try regular feed
              this.tryRegularImageFeed(incident, imageUri, showLoading);
            }
          });
        } else if (imageUri) {
          // No zoomed feed, try regular feed
          this.tryRegularImageFeed(incident, imageUri, showLoading);
        } else {
          // No feeds available, use static fallbacks
          this.useStaticImageFallback(incident, showLoading);
        }
        // No rotation for incidents
      },
      error: (err) => {
        console.error('Error loading incident:', err);
        if (showLoading) {
          this.error = true;
          this.loading = false;
          this.cameraName = 'Error loading incident';
        }
      }
    });
  }

  private fetchLatestImageFromUri(uri: string) {
    return new Observable<string | null>(observer => {
      const request = {
        period: 'latest',
        type: 'media',
        timeZoneOffset: this.app.getTimeZoneOffset(null),
        forceEmbed: false,
        index: 0,
        publishedIdentifier: null,
        siteUri: null,
        timeSeries: {
          single: {
            variable: {
              uri: uri,
              transforms: null,
            }
          }
        }
      };

      let apiCall;
      if (this.publishedIdentifier) {
        apiCall = this.openPublishedCameraWatchService.PublishedCameraWatchGetData({
          identifier: this.publishedIdentifier,
          request
        });
      } else {
        apiCall = this.dataService.DataGetData({
          customerId: this.app.customerId,
          request
        });
      }

      apiCall.subscribe({
        next: (r) => {
          if (r.values && r.values.length > 0) {
            // Use the last image returned
            observer.next(r.values[r.values.length - 1].valueStr);
          } else {
            observer.next(null);
          }
          observer.complete();
        },
        error: (err) => {
          observer.error(err);
        }
      });
    });
  }

  private tryRegularImageFeed(incident: Incident, imageUri: string | undefined, showLoading: boolean): void {
    if (imageUri) {
      this.fetchLatestImageFromUri(imageUri).subscribe({
        next: (imageUrl) => {
          if (imageUrl) {
            this.currentImageUrl = imageUrl;
            this.isZoomedImage = false;
          } else {
            // No results from regular feed, use static fallbacks
            this.useStaticImageFallback(incident, showLoading);
          }
          
          if (showLoading) {
            this.loading = false;
          }
        },
        error: (err) => {
          console.error('Error loading regular incident images:', err);
          // Error with regular feed, use static fallbacks
          this.useStaticImageFallback(incident, showLoading);
          
          if (showLoading) {
            this.loading = false;
          }
        }
      });
    } else {
      this.useStaticImageFallback(incident, showLoading);
      if (showLoading) {
        this.loading = false;
      }
    }
  }

  private useStaticImageFallback(incident: Incident, showLoading: boolean): void {
    if (incident.firstZoomedImageUrl) {
      this.currentImageUrl = incident.firstZoomedImageUrl;
      this.isZoomedImage = true;
    } else if (incident.firstImageUrl) {
      this.currentImageUrl = incident.firstImageUrl;
      this.isZoomedImage = false;
    } else {
      if (showLoading) {
        this.error = true;
        this.cameraName = 'No image available';
      }
    }
  }

  private loadIncidentStatus(showLoading: boolean = true): void {
    if (showLoading) {
      this.loading = true;
      this.error = false;
    }

    const statusId = this.item.statusId;
    if (!statusId) {
      console.error('No statusId provided for incident-status type');
      this.error = true;
      this.loading = false;
      return;
    }

    // Use appropriate service based on publishedIdentifier
    let apiCall;
    if (this.publishedIdentifier) {
      apiCall = this.openPublishedCameraWatchService.PublishedCameraWatchGetIncidents({
        identifier: this.publishedIdentifier,
        statusId: statusId
      });
    } else {
      apiCall = this.fireForesightService.FireForesightGetIncidents({
        customerId: this.app.customerId,
        statusId: statusId,
        siteUri: this.item.uri
      });
    }

    apiCall.subscribe({
      next: (incidents: Incident[]) => {
   
        // Prioritize new incidents (ones not in our known set)
        const newIncidents = incidents.filter(i => !this.knownIncidentIds.has(i.incidentId));
        const existingIncidents = incidents.filter(i => this.knownIncidentIds.has(i.incidentId));
        
        // Update our known incidents set
        this.knownIncidentIds = new Set(incidents.map(i => i.incidentId));
        
        // Put new incidents first, then existing ones
        this.statusIncidents = [...newIncidents, ...existingIncidents];
        
        if (this.statusIncidents.length === 0) {
          this.cameraName = `${this.item.statusName || 'Status'}: No incidents`;
          // Don't set error=true for empty results, just stop loading
          if (showLoading) {
            this.loading = false;
          }
          return;
        }

        // Reset to first incident if this is initial load or if incidents changed significantly
        if (showLoading || this.currentStatusIncidentIndex >= this.statusIncidents.length) {
          this.currentStatusIncidentIndex = 0;
        }

        // Start rotation timer (only once)
        if (!this.statusRotationSubscription) {
          this.startStatusRotation();
        }

        // Start refresh timer (only once)
        if (!this.statusRefreshSubscription) {
          this.startStatusRefresh();
        }

        // Load the current incident
        this.loadCurrentStatusIncident(showLoading);
      },
      error: (err) => {
        console.error('Error loading incidents for status:', err);
        this.error = true;
        if (showLoading) {
          this.loading = false;
        }
        this.cameraName = 'Error loading incidents';
      }
    });
  }

  private startStatusRotation(): void {
    this.statusRotationSubscription = interval(this.STATUS_ROTATION_INTERVAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.rotateToNextStatusIncident();
      });
  }

  private startStatusRefresh(): void {
    this.statusRefreshSubscription = interval(this.STATUS_REFRESH_INTERVAL)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Refresh the incidents list
        this.loadIncidentStatus(false);
      });
  }

  private rotateToNextStatusIncident(): void {
    if (this.statusIncidents.length === 0) return;
    
    this.currentStatusIncidentIndex = (this.currentStatusIncidentIndex + 1) % this.statusIncidents.length;
    this.loadCurrentStatusIncident(false);
  }

  private loadCurrentStatusIncident(showLoading: boolean = true): void {
    if (this.statusIncidents.length === 0) return;
    
    const currentIncident = this.statusIncidents[this.currentStatusIncidentIndex];
    this.incident = currentIncident;
    
    // Extract detection metadata for overlay
    console.log('[WatchCameraView - Status] Incident detections:', currentIncident.detections?.length || 0);
    if (currentIncident.detections && currentIncident.detections.length > 0) {
      this.overlayConfig = currentIncident.detections[currentIncident.detections.length - 1].detectionMetaData;
      console.log('[WatchCameraView - Status] Overlay config set:', this.overlayConfig);
    } else {
      this.overlayConfig = null;
      console.log('[WatchCameraView - Status] No detections available for overlay');
    }
    
    // Update camera name to show current incident info
    const incidentCount = this.statusIncidents.length;
    const incidentNumber = this.currentStatusIncidentIndex + 1;
    this.cameraName = `${this.item.statusName || 'Status'}: ${currentIncident.site?.name || 'Incident'} (${incidentNumber}/${incidentCount})`;
    
    // Try to load images using the cascade: zoomedImageFeedUri -> imageFeedUri -> static
    const zoomedImageUri = currentIncident.zoomedImageFeedUri;
    const imageUri = currentIncident.imageFeedUri;
    
    if (zoomedImageUri) {
      this.fetchLatestImageFromUri(zoomedImageUri).subscribe({
        next: (imageUrl) => {
          if (imageUrl) {
            this.currentImageUrl = imageUrl;
            this.isZoomedImage = true;
            if (showLoading) {
              this.loading = false;
            }
          } else {
            this.tryRegularImageFeedForStatusIncident(currentIncident, imageUri, showLoading);
          }
        },
        error: (err) => {
          console.error('Error loading zoomed incident images:', err);
          this.tryRegularImageFeedForStatusIncident(currentIncident, imageUri, showLoading);
        }
      });
    } else if (imageUri) {
      this.tryRegularImageFeedForStatusIncident(currentIncident, imageUri, showLoading);
    } else {
      this.useStaticImageFallback(currentIncident, showLoading);
      if (showLoading) {
        this.loading = false;
      }
    }
  }

  private tryRegularImageFeedForStatusIncident(incident: Incident, imageUri: string | undefined, showLoading: boolean): void {
    if (imageUri) {
      this.fetchLatestImageFromUri(imageUri).subscribe({
        next: (imageUrl) => {
          if (imageUrl) {
            this.currentImageUrl = imageUrl;
            this.isZoomedImage = false;
          } else {
            this.useStaticImageFallback(incident, showLoading);
          }
          
          if (showLoading) {
            this.loading = false;
          }
        },
        error: (err) => {
          console.error('Error loading regular incident images:', err);
          this.useStaticImageFallback(incident, showLoading);
          
          if (showLoading) {
            this.loading = false;
          }
        }
      });
    } else {
      this.useStaticImageFallback(incident, showLoading);
      if (showLoading) {
        this.loading = false;
      }
    }
  }

  private rotateToNextImage(): void {
    // Only rotate for cameras, not incidents
    if (this.item.type !== 'camera' || this.cameraImages.length === 0) return;
    
    this.currentImageIndex = (this.currentImageIndex + 1) % this.cameraImages.length;
    this.updateCurrentImageWithTransition('forward');
  }

  private updateCurrentImage(): void {
    if (this.cameraImages.length > 0 && this.currentImageIndex < this.cameraImages.length) {
      const image = this.cameraImages[this.currentImageIndex];
      this.currentImageUrl = image.latestImageUrl || '';
      this.preloadedUrls.add(this.currentImageUrl);
      this.preloadNextImage();
    }
  }

  /** Preload the image following the current one so it's cached before the next rotation/navigation. */
  private preloadNextImage(): void {
    if (this.cameraImages.length <= 1) return;
    const nextIndex = (this.currentImageIndex + 1) % this.cameraImages.length;
    const nextUrl = this.cameraImages[nextIndex]?.latestImageUrl;
    this.preloadImage(nextUrl);
  }

  private preloadImage(url: string | undefined): void {
    if (!url || this.preloadedUrls.has(url)) return;
    const img = new Image();
    img.onload = () => this.preloadedUrls.add(url);
    img.onerror = () => this.preloadedUrls.add(url);
    img.src = url;
  }

  private updateCurrentImageWithTransition(direction: 'forward' | 'backward' = 'forward'): void {
    if (this.cameraImages.length > 0 && this.currentImageIndex < this.cameraImages.length) {
      const image = this.cameraImages[this.currentImageIndex];
      const newUrl = image.latestImageUrl || '';
      
      // Only transition if the URL actually changed
      if (newUrl !== this.currentImageUrl) {
        this.transitionDirection = direction;

        const startTransition = () => {
          this.nextImageUrl = newUrl;
          this.isTransitioning = true;

          // After animation completes, swap images and preload the following one
          setTimeout(() => {
            this.currentImageUrl = this.nextImageUrl;
            this.nextImageUrl = '';
            this.isTransitioning = false;
            this.preloadNextImage();
          }, 500); // Match CSS animation duration
        };

        if (this.preloadedUrls.has(newUrl)) {
          // Already cached from a previous preload - transition immediately.
          startTransition();
        } else {
          // Not yet cached (e.g. rapid manual navigation) - wait for the image to load first.
          const img = new Image();
          img.onload = () => { this.preloadedUrls.add(newUrl); startTransition(); };
          img.onerror = () => { this.preloadedUrls.add(newUrl); startTransition(); };
          img.src = newUrl;
        }
      }
    }
  }

  private refreshImage(): void {
    // Reload the data to get fresh image URLs without showing loading state
    if (this.item.type === 'incident-status') {
      // For status feeds, refresh the incidents list
      this.loadIncidentStatus(false);
    } else {
      this.loadItemData(false);
    }
  }

  public onRemoveClick(): void {
    this.remove.emit(this.item);
  }

  public toggleFullscreen(): void {
    this.isFullscreen = !this.isFullscreen;
  }

  public exitFullscreen(): void {
    this.isFullscreen = false;
  }

  public navigateToPrevious(): void {
    if (this.item.type !== 'camera' || this.cameraImages.length === 0) return;
    
    this.currentImageIndex = (this.currentImageIndex - 1 + this.cameraImages.length) % this.cameraImages.length;
    this.updateCurrentImageWithTransition('backward');
  }

  public navigateToNext(): void {
    if (this.item.type !== 'camera' || this.cameraImages.length === 0) return;
    
    this.currentImageIndex = (this.currentImageIndex + 1) % this.cameraImages.length;
    this.updateCurrentImageWithTransition('forward');
  }

  public get currentBearing(): number | undefined {
    if (this.item.type === 'camera' && this.cameraImages.length > 0) {
      return this.cameraImages[this.currentImageIndex]?.bearing;
    }
    return undefined;
  }

  public onImageError(): void {
    this.error = true;
    this.overlays = [];
  }

  public onImageLoad(): void {
    this.error = false;
    if (this.overlayConfig) {
      this.processOverlayConfig();
    }
  }

  private processOverlayConfig(): void {
    if (!this.overlayConfig) {
      this.overlays = [];
      return;
    }

    try {
      console.log('[WatchCameraView] Processing overlay config:', this.overlayConfig);
      const imageOverlay = JSON.parse(this.overlayConfig);
      console.log('[WatchCameraView] Parsed overlay:', imageOverlay);

      // Load image to get dimensions
      const img = new Image();
      img.onload = () => {
        this.overlays = [];
        const imgWidth = img.width;
        const imgHeight = img.height;
        console.log('[WatchCameraView] Image dimensions:', imgWidth, 'x', imgHeight);

        if (imageOverlay.objects) {
          for (let obj of imageOverlay.objects) {
            const startX = (obj.rectangle.x / imgWidth) * 100;
            const startY = (obj.rectangle.y / imgHeight) * 100;
            const width = (obj.rectangle.w / imgWidth) * 100;
            const height = (obj.rectangle.h / imgHeight) * 100;
            this.overlays.push({
              x: startX,
              y: startY,
              w: width,
              h: height,
              name: obj.object,
              confidence: obj.confidence
            });
          }
          console.log('[WatchCameraView] Overlays created:', this.overlays);
        }
      };
      img.src = this.currentImageUrl;
    } catch (err) {
      console.error('[WatchCameraView] Error processing overlay config:', err);
      this.overlays = [];
    }
  }
}
